import React from 'react';
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { FoundScanRow } from './Stage3JuryPairs.types';

type Props = {
	busy: boolean;
	onVerifySecretaries: () => void;
	onVerifyJuryFolders: () => void;
	secretaryVerifyReport: any | null;
	juryVerifyReport: any | null;
	verifySortMode: 'districts' | 'pairs' | 'statuses';
	setVerifySortMode: (mode: 'districts' | 'pairs' | 'statuses') => void;
	verifyFilterDistrictId: number;
	setVerifyFilterDistrictId: (districtId: number) => void;
	verifyFilterStatus: 'new_only' | 'AUTO-SKAN' | 'AUTOSKANOK' | 'not_copied' | 'all_scanned';
	setVerifyFilterStatus: (status: 'new_only' | 'AUTO-SKAN' | 'AUTOSKANOK' | 'not_copied' | 'all_scanned') => void;
	verifyDistrictOptions: { id: number; name: string }[];
	foundScanRows: FoundScanRow[];
	onOpenInlinePreview: (row: FoundScanRow) => void;
	onMarkAutoScanOkSingle: (row: FoundScanRow) => void;
	onMarkAutoScanOkDistrict: (districtId: number, districtName: string) => void;
};

const Stage3DriveVerificationTab = ({
	busy,
	onVerifySecretaries,
	onVerifyJuryFolders,
	secretaryVerifyReport,
	juryVerifyReport,
	verifySortMode,
	setVerifySortMode,
	verifyFilterDistrictId,
	setVerifyFilterDistrictId,
	verifyFilterStatus,
	setVerifyFilterStatus,
	verifyDistrictOptions,
	foundScanRows,
	onOpenInlinePreview,
	onMarkAutoScanOkSingle,
	onMarkAutoScanOkDistrict,
}: Props) => {
	const secretarySummary = secretaryVerifyReport?.summary;
	const secretaryDistricts: any[] = Array.isArray(secretaryVerifyReport?.districts)
		? secretaryVerifyReport.districts
		: [];

	const juryPairsReport: any[] = Array.isArray(juryVerifyReport?.jury_pairs) ? juryVerifyReport.jury_pairs : [];
	const jurySummary = juryVerifyReport?.summary;

	return (
		<>
			<Typography variant='subtitle1'>Weryfikacja skanów</Typography>
			<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				<Button
					variant='outlined'
					onClick={onVerifySecretaries}
					disabled={busy}
				>
					Wczytaj dane
				</Button>
				<Button
					variant='outlined'
					onClick={onVerifyJuryFolders}
					disabled={busy}
				>
					Weryfikuj foldery komisji
				</Button>
			</Box>

			{secretaryVerifyReport && (
				<Box sx={{ mt: 2 }}>
					{secretarySummary && (
						<TableContainer
							component={Paper}
							sx={{ mb: 1 }}
						>
							<Table size='small'>
								<TableHead>
									<TableRow>
										<TableCell>Oczekiwanych</TableCell>
										<TableCell>Odnotowanych wcześniej</TableCell>
										<TableCell>Znalezionych</TableCell>
										<TableCell>Nowo znalezionych</TableCell>
										<TableCell>Ustawiono AUTO-SKAN</TableCell>
										<TableCell>Brakujących</TableCell>
										<TableCell>Nieoczekiwanych</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell>{secretarySummary.expected_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.noted_before_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.found_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.newly_found_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.auto_scan_updated_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.missing_total ?? '-'}</TableCell>
										<TableCell>{secretarySummary.unexpected_total ?? '-'}</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					)}

					{secretaryDistricts.length > 0 && (
						<details>
							<summary>Szczegóły per okręg ({secretaryDistricts.length})</summary>
							<TableContainer
								component={Paper}
								sx={{ mt: 1, maxHeight: 260 }}
							>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>Okręg</TableCell>
											<TableCell>Oczekiwanych</TableCell>
											<TableCell>Znalezionych</TableCell>
											<TableCell>Pasujących</TableCell>
											<TableCell>Brakujących</TableCell>
											<TableCell>Nieoczekiwanych</TableCell>
											<TableCell>Folder OK</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{secretaryDistricts.map((d: any) => (
											<TableRow key={d.district_id}>
												<TableCell>{d.district_name}</TableCell>
												<TableCell>{d.expected_count ?? '-'}</TableCell>
												<TableCell>{d.found_count ?? '-'}</TableCell>
												<TableCell>{d.matched_count ?? '-'}</TableCell>
												<TableCell>{(d.missing_codes?.length ?? 0) > 0 ? d.missing_codes.length : '-'}</TableCell>
												<TableCell>{(d.unexpected_codes?.length ?? 0) > 0 ? d.unexpected_codes.length : '-'}</TableCell>
												<TableCell>{d.ok ? 'TAK' : d.error || 'NIE'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</details>
					)}

					<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
						<FormControl size='small'>
							<InputLabel id='verify-sort-mode-label'>Sortuj skany</InputLabel>
							<Select
								labelId='verify-sort-mode-label'
								label='Sortuj skany'
								value={verifySortMode}
								onChange={(e) => setVerifySortMode(e.target.value as 'districts' | 'pairs' | 'statuses')}
								sx={{ minWidth: 220 }}
							>
								<MenuItem value='districts'>Lista okręgów</MenuItem>
								<MenuItem value='pairs'>Lista komisji</MenuItem>
								<MenuItem value='statuses'>Lista statusów</MenuItem>
							</Select>
						</FormControl>
						<FormControl size='small'>
							<InputLabel id='verify-district-filter-label'>Filtr okręgu</InputLabel>
							<Select
								labelId='verify-district-filter-label'
								label='Filtr okręgu'
								value={verifyFilterDistrictId}
								onChange={(e) => setVerifyFilterDistrictId(Number(e.target.value))}
								sx={{ minWidth: 220 }}
							>
								<MenuItem value={0}>Wszystkie okręgi</MenuItem>
								{verifyDistrictOptions.map((district) => (
									<MenuItem
										key={district.id}
										value={district.id}
									>
										{district.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size='small'>
							<InputLabel id='verify-status-filter-label'>Filtr statusu</InputLabel>
							<Select
								labelId='verify-status-filter-label'
								label='Filtr statusu'
								value={verifyFilterStatus}
								onChange={(e) =>
									setVerifyFilterStatus(
										e.target.value as 'new_only' | 'AUTO-SKAN' | 'AUTOSKANOK' | 'not_copied' | 'all_scanned',
									)
								}
								sx={{ minWidth: 220 }}
							>
								<MenuItem value='new_only'>Nowe</MenuItem>
								<MenuItem value='AUTO-SKAN'>Ze statusem AUTO-SKAN</MenuItem>
								<MenuItem value='AUTOSKANOK'>Ze statusem AUTOSKANOK</MenuItem>
								<MenuItem value='not_copied'>Nieskopiowane jurorom</MenuItem>
								<MenuItem value='all_scanned'>Wszystkie przeskanowane u sekretarzy</MenuItem>
							</Select>
						</FormControl>
					</Box>
					{foundScanRows.length > 0 && (
						<TableContainer
							component={Paper}
							sx={{ mt: 1, maxHeight: 320 }}
						>
							<Table
								size='small'
								stickyHeader
							>
								<TableHead>
									<TableRow>
										<TableCell>Okręg</TableCell>
										<TableCell>Komisja</TableCell>
										<TableCell>Kod</TableCell>
										<TableCell>ID ucznia</TableCell>
										<TableCell>Ścieżka/plik</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Skopiowano jurorom</TableCell>
										<TableCell>Akcje</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{foundScanRows.map((row) => (
										<TableRow key={`${row.district_id}-${row.work_code}-${row.source_file_id}`}>
											<TableCell>{row.district_name}</TableCell>
											<TableCell>{row.jury_pair_name || '-'}</TableCell>
											<TableCell>{row.work_code}</TableCell>
											<TableCell>{row.student_id || '-'}</TableCell>
											<TableCell>{row.source_relative_path || row.source_file_name || '-'}</TableCell>
											<TableCell>{row.existing_status || '-BRAK-'}</TableCell>
											<TableCell>{row.drive_copied ? 'TAK' : 'NIE'}</TableCell>
											<TableCell>
												<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
													<Button
														size='small'
														variant='outlined'
														onClick={() => onOpenInlinePreview(row)}
														disabled={busy}
													>
														Podgląd
													</Button>
													<Button
														size='small'
														variant='outlined'
														onClick={() => onMarkAutoScanOkSingle(row)}
														disabled={busy || row.existing_status !== 'AUTO-SKAN'}
													>
														AUTOSKANOK
													</Button>
													<Button
														size='small'
														variant='text'
														onClick={() => onMarkAutoScanOkDistrict(row.district_id, row.district_name)}
														disabled={busy}
													>
														Cały okręg
													</Button>
												</Box>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>
			)}

			{juryVerifyReport && (
				<Box sx={{ mt: 2 }}>
					<Typography variant='subtitle2'>Raport folderów komisji</Typography>
					{jurySummary && (
						<Typography
							variant='body2'
							sx={{ mb: 1 }}
						>
							Komisji: {jurySummary.pair_count ?? '-'}, brakujących prac łącznie: {jurySummary.missing_total ?? '-'},
							nieoczekiwanych łącznie: {jurySummary.unexpected_total ?? '-'}
						</Typography>
					)}
					{juryPairsReport.length > 0 && (
						<TableContainer
							component={Paper}
							sx={{ maxHeight: 280 }}
						>
							<Table
								size='small'
								stickyHeader
							>
								<TableHead>
									<TableRow>
										<TableCell>Komisja</TableCell>
										<TableCell>Folder OK</TableCell>
										<TableCell>Oczekiwanych</TableCell>
										<TableCell>Znalezionych</TableCell>
										<TableCell>Pasujących</TableCell>
										<TableCell>Brakujących</TableCell>
										<TableCell>Nieoczekiwanych</TableCell>
										<TableCell>Duplikaty</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{juryPairsReport.map((p: any) => (
										<TableRow key={p.jury_pair_id}>
											<TableCell>{p.jury_pair_name}</TableCell>
											<TableCell>{p.ok ? 'TAK' : p.error || 'NIE'}</TableCell>
											<TableCell>{p.expected_non_paper_count ?? '-'}</TableCell>
											<TableCell>{p.found_count ?? '-'}</TableCell>
											<TableCell>{p.matched_count ?? '-'}</TableCell>
											<TableCell>{(p.missing_codes?.length ?? 0) > 0 ? p.missing_codes.join(', ') : '-'}</TableCell>
											<TableCell>
												{(p.unexpected_codes?.length ?? 0) > 0 ? p.unexpected_codes.join(', ') : '-'}
											</TableCell>
											<TableCell>
												{(p.duplicate_code_files?.length ?? 0) > 0
													? p.duplicate_code_files.map((d: any) => d.code || d).join(', ')
													: '-'}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>
			)}
		</>
	);
};

export default Stage3DriveVerificationTab;
